/*
 * ProjectManager — Business-Logik für Multi-Projekt-Verwaltung
 *
 * Vermittelt zwischen IndexedDBStore und dem Editor.
 * Verwaltet das aktive Projekt, Dirty-Tracking mit Debounce,
 * und die einmalige Migration von localStorage zu IndexedDB.
 */

import { generateUUID } from './uuid.js'
import { serializeParagraphRT } from '../RichTextOps.js'
import { measure, mark } from './syncPerf.js'
import { remapParagraphIds, hashParagraphData } from './paragraphUtils.js'

/**
 * Schaetzt die Speichergroesse eines serialisierten Paragraphen.
 * Variante 1 aus Spec §2.2.1 — JSON.stringify-basiert, dieselbe Quelle
 * wie der Sync-Upload sieht. Genau genug fuer die Dashboard-Anzeige.
 * @param {any} data
 * @returns {number}
 */
function paragraphSize(data) {
    try { return JSON.stringify(data).length }
    catch { return 0 }
}

/**
 * Summiert die Groessen einer Paragraph-Liste (Helper fuer duplicateProject
 * und Backfill).
 * @param {any[]} dataArray
 */
function sumParagraphSizes(dataArray) {
    let s = 0
    for (const d of dataArray) s += paragraphSize(d)
    return s
}

// localStorage-Keys (nur für Migration und Projekt-Referenz)
const CONTENT_KEY = 'carbisdale_content'
const EDITOR_STATE_KEY = 'carbisdale_editor_state'
const MIGRATED_KEY = 'carbisdale_migrated'
const LAST_PROJECT_KEY = 'carbisdale_last_project'
const TABS_KEY = 'carbisdale_tabs'

// Debounce-Delay für IndexedDB-Flush
const FLUSH_DELAY = 1000  // 1 Sekunde nach letzter Änderung

export class ProjectManager {
    /**
     * @param {import('./IndexedDBStore.js').IndexedDBStore} store
     * @param {string} userId - ID des eingeloggten Users
     */
    constructor(store, userId) {
        this._store = store
        this._userId = userId
        this.currentProjectId = null

        // Dirty-Tracking: geänderte Paragraph-IDs
        this._dirtyParagraphs = new Set()
        this._flushTimeout = null

        // Strukturelle Aenderung: paragraphOrder hat sich geaendert,
        // neue Absaetze muessen erkannt werden
        this._structuralDirty = false

        // Aktuell geladene Projekt-Daten (Cache)
        this._currentProject = null

        // In-Memory Paragraph-Meta-Cache fuer das aktive Projekt — entweder
        // null (ungueltig) oder vollstaendig konsistent mit der DB.
        //
        // Layout: Map<id, {version: number, hash: number}>
        // - version: laufende Versionsnummer (Lesson 45: Bump nur bei echter
        //   Content-Aenderung)
        // - hash: FNV-1a-32 des serialisierten Content (Lesson 50: erlaubt
        //   praezise Skip-Logik im flush, damit Undo nicht tausende
        //   unveraenderte Paragraphen schreibt)
        //
        // Wird bei Remote-Sync (onRemoteChanges) invalidiert.
        this._paragraphMeta = null

        // SyncService-Referenz (optional, wird per setSyncService gesetzt)
        this._syncService = null
    }

    // ─────────────────────────────────────────────
    // Initialisierung
    // ─────────────────────────────────────────────

    /**
     * Öffnet die DB, migriert ggf. von localStorage,
     * und lädt das zuletzt aktive Projekt.
     * @returns {Promise<{project: Object, paragraphs: Object[]}>}
     */
    async initialize() {
        await this._store.open()

        // Einmalige Migration von localStorage
        const migrated = localStorage.getItem(MIGRATED_KEY)
        if (!migrated) {
            await this._migrateFromLocalStorage()
        }

        // Bestehende Projekte ohne userId dem aktuellen User zuweisen
        await this._migrateUserIds()

        // Papierkorb aufräumen (>30 Tage)
        await this.cleanupTrash(30)

        // Letztes aktives Projekt laden (nur eigene Projekte)
        const lastProjectId = localStorage.getItem(LAST_PROJECT_KEY)
        const projects = await this._store.getAllProjects(this._userId)

        if (projects.length === 0) {
            // Kein Projekt vorhanden → neues erstellen
            const projectId = await this.createProject('untitled')
            return this.loadProject(projectId)
        }

        // Letztes Projekt wiederherstellen oder das neueste nehmen
        const target = lastProjectId && projects.find(p => p.id === lastProjectId)
            ? lastProjectId
            : projects.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))[0].id

        return this.loadProject(target)
    }

    // ─────────────────────────────────────────────
    // Projekt-Lifecycle
    // ─────────────────────────────────────────────

    /**
     * Lädt ein Projekt mit allen Paragraphen.
     * @param {string} projectId
     * @returns {Promise<{project: Object, paragraphs: Object[]}>}
     */
    async loadProject(projectId) {
        const project = await this._store.getProject(projectId)
        if (!project) throw new Error(`Projekt ${projectId} nicht gefunden`)

        // Defense-in-Depth: Projekte anderer User liegen in derselben
        // IndexedDB (geteilter Browser) — laden darf sie nur ihr Besitzer.
        // Projekte ohne userId sind Alt-Daten vor _migrateUserIds und erlaubt.
        if (project.userId && project.userId !== this._userId) {
            throw new Error(`Projekt ${projectId} gehört einem anderen User`)
        }

        const rawParagraphs = await this._store.getParagraphsByProject(projectId)

        // Paragraphen in der richtigen Reihenfolge sortieren
        const orderMap = new Map()
        if (project.paragraphOrder) {
            project.paragraphOrder.forEach((id, i) => orderMap.set(id, i))
        }
        const paragraphs = rawParagraphs.sort((a, b) => {
            const posA = orderMap.has(a.id) ? orderMap.get(a.id) : Infinity
            const posB = orderMap.has(b.id) ? orderMap.get(b.id) : Infinity
            return posA - posB
        })

        // Serialisierte Daten in das Format bringen, das setParagraphsRT erwartet
        const paragraphData = paragraphs.map(p => ({
            ...p.data,
            id: p.id
        }))

        this.currentProjectId = projectId
        this._currentProject = project
        // sizeBytes-Feld nachruesten fuer Projekte, die vor Migration 007
        // angelegt wurden (oder noch nie geflusht wurden). Backfill-Task
        // berechnet das fuer alle Projekte korrekt; hier reicht 0 als
        // Fallback, damit das Feld nie undefined ist.
        if (typeof this._currentProject.sizeBytes !== 'number') {
            this._currentProject.sizeBytes = 0
        }
        if (this._currentProject.folderId === undefined) {
            this._currentProject.folderId = null
        }
        this._dirtyParagraphs.clear()
        // Paragraph-Meta frisch aus dem ohnehin geladenen Paragraph-Set aufbauen.
        // Content-Hashes werden direkt aus p.data berechnet (das gleiche Format,
        // das auch flush() schreibt). Kein zusaetzlicher IDB-Read noetig.
        // sizeBytes pro Paragraph: Variante 1 aus Spec §2.2.1, im Hot-Path
        // genauso "billig" wie der Hash-Build (eine JSON.stringify-Operation
        // ueber p.data, im Mikrosekunden-Bereich).
        this._paragraphMeta = new Map(rawParagraphs.map(p => [p.id, {
            version: p.version || 1,
            hash: hashParagraphData(p.data),
            sizeBytes: paragraphSize(p.data)
        }]))
        localStorage.setItem(LAST_PROJECT_KEY, projectId)

        return { project, paragraphs: paragraphData }
    }

    /**
     * Invalidiert den In-Memory Paragraph-Meta-Cache, wenn das betroffene
     * Projekt aktiv ist. Wird vom SyncService-onRemoteChanges-Callback
     * aufgerufen, nachdem Server-seitige Aenderungen in die lokale DB
     * geschrieben wurden.
     *
     * Name aus Backward-Compat-Gruenden beibehalten — main.js und Tests
     * rufen weiterhin `invalidateVersionMap` auf.
     * @param {string} projectId
     */
    invalidateVersionMap(projectId) {
        if (projectId === this.currentProjectId) {
            this._paragraphMeta = null
        }
    }

    /**
     * Synchroner Versions-Lookup fuer einen einzelnen Paragraphen des aktiven
     * Projekts. Gibt undefined zurueck, wenn der Cache noch nicht aufgebaut
     * ist oder die ID unbekannt ist.
     *
     * Wird vom ReviewController fuer den Auto-Apply Versions-Check benutzt —
     * `paragraph.version` selbst lebt nicht im EditorModel, der Cache hier ist
     * die einzige In-Memory-Quelle der Wahrheit.
     *
     * @param {string} paragraphId
     * @returns {number|undefined}
     */
    getParagraphVersion(paragraphId) {
        if (!this._paragraphMeta) return undefined
        return this._paragraphMeta.get(paragraphId)?.version
    }

    /**
     * Liefert eine Map<id, version> aller Paragraphen eines Projekts.
     * Fuer das aktive Projekt wird der In-Memory-Cache genutzt (0 DB-Cost)
     * und auf die Version-Spalte projiziert; fuer inaktive Projekte faellt
     * der Aufruf auf den Store zurueck.
     *
     * Rueckgabe ist immer eine Kopie — der Aufrufer (typischerweise
     * SyncService.syncProject) darf sie mutieren, ohne den Cache zu
     * kompromittieren.
     *
     * @param {string} projectId
     * @returns {Promise<Map<string, number>>}
     */
    async getVersionMap(projectId) {
        if (projectId === this.currentProjectId && this._paragraphMeta) {
            const out = new Map()
            for (const [id, meta] of this._paragraphMeta) out.set(id, meta.version)
            return out
        }
        return this._store.getParagraphIdVersionMap(projectId)
    }

    /**
     * Erstellt ein neues Projekt.
     * @param {string} title
     * @param {string} [author='']
     * @param {string|null} [folderId=null] - Folder-UUID, null = Root ("My Projects")
     * @returns {Promise<string>} Projekt-UUID
     */
    async createProject(title, author = '', folderId = null) {
        const id = generateUUID()
        const now = new Date().toISOString()

        const project = {
            id,
            title,
            author,
            userId: this._userId,
            paragraphOrder: [],
            cursorState: null,
            cloudEnabled: false,
            version: 1,
            syncStatus: 'local',
            createdAt: now,
            updatedAt: now,
            deletedAt: null,
            // Dashboard Phase 4: Folder-Zuordnung und gecachte Gesamt-
            // Groesse aller Paragraph-Daten (Bytes). Wird inkrementell
            // beim flush() aktualisiert — siehe Spec §2.2.1.
            folderId: folderId || null,
            sizeBytes: 0,
            // Pro-Projekt-Spellcheck (Spec §6.2 / §7.4). null heisst
            // "User-Default verwenden" — den Wert kennt das Frontend beim
            // switchToProject und reicht ihn dem SpellCheckManager weiter.
            spellcheckLang: null,
            spellcheckIgnored: []
        }

        await this._store.putProject(project)
        return id
    }

    /**
     * Setzt die Pro-Projekt-Spellcheck-Sprache. `null` bedeutet "User-Default".
     * Der Sync wird per `markSpellcheckMetaDirty` getriggert, sobald der
     * naechste Sync laeuft.
     * @param {string} projectId
     * @param {string|null} lang
     */
    async setProjectSpellcheckLang(projectId, lang) {
        const project = await this._store.getProject(projectId)
        if (!project) return
        if ((project.spellcheckLang || null) === (lang || null)) return
        project.spellcheckLang = lang || null
        project.updatedAt = new Date().toISOString()
        await this._store.putProject(project)
        if (this.currentProjectId === projectId && this._currentProject) {
            this._currentProject.spellcheckLang = lang || null
        }
        if (this._syncService && typeof this._syncService.markSpellcheckMetaDirty === 'function') {
            this._syncService.markSpellcheckMetaDirty(projectId)
        }
    }

    /**
     * Setzt die Pro-Projekt-Ignore-Liste. Replace-Semantik (genau wie
     * Personal-Dict). Sync wird beim naechsten Sync mitgeschickt.
     * @param {string} projectId
     * @param {string[]} words
     */
    async setProjectSpellcheckIgnore(projectId, words) {
        const project = await this._store.getProject(projectId)
        if (!project) return
        const list = Array.isArray(words)
            ? Array.from(new Set(words.filter(w => typeof w === 'string' && w.length > 0))).sort()
            : []
        project.spellcheckIgnored = list
        project.updatedAt = new Date().toISOString()
        await this._store.putProject(project)
        if (this.currentProjectId === projectId && this._currentProject) {
            this._currentProject.spellcheckIgnored = list
        }
        if (this._syncService && typeof this._syncService.markSpellcheckMetaDirty === 'function') {
            this._syncService.markSpellcheckMetaDirty(projectId)
        }
    }

    /**
     * Dupliziert ein Projekt mit neuen UUIDs.
     * @param {string} sourceId
     * @returns {Promise<string>} Neue Projekt-UUID
     */
    async duplicateProject(sourceId) {
        const source = await this._store.getProject(sourceId)
        if (!source) throw new Error(`Projekt ${sourceId} nicht gefunden`)

        const sourceParagraphs = await this._store.getParagraphsByProject(sourceId)
        const newProjectId = generateUUID()
        const now = new Date().toISOString()

        // Neue UUIDs + Section-Link-Remap in einem Pass via Helper.
        // linksPath='data' weil Store-Format: Links unter p.data.links.
        const { paragraphs: remapped, idMap } = remapParagraphIds(sourceParagraphs, { linksPath: 'data' })

        // Store-Metadaten (projectId, version, updatedAt) auf neue Werte setzen.
        const newParagraphs = remapped.map(p => ({
            id: p.id,
            projectId: newProjectId,
            data: p.data,
            version: 1,
            updatedAt: now
        }))

        // paragraphOrder mit neuen IDs
        const newOrder = (source.paragraphOrder || []).map(id => idMap.get(id) || id)

        // Title mit "(copy)"-Suffix — Spec §11.6: bei weiterer Duplizierung
        // "(2. copy)", "(3. copy)" etc. Duplikat-Erkennung via Folder-Inhalt:
        // wir laden Geschwister-Projekte und suchen das hoechste Suffix.
        const newTitle = await this._suggestDuplicateTitle(source.title, source.folderId)

        const newProject = {
            id: newProjectId,
            title: newTitle,
            author: source.author,
            userId: this._userId,
            paragraphOrder: newOrder,
            cursorState: null,
            // Duplicate startet lokal; der Dashboard-Aufrufer hebt es danach
            // via _autoCloudNewProject in die Cloud (cloud-first 2026-07-02,
            // ersetzt Spec §5.1 "landet immer lokal").
            cloudEnabled: false,
            version: 1,
            syncStatus: 'local',
            createdAt: now,
            updatedAt: now,
            deletedAt: null,
            // Dashboard Phase 4: Duplikat erbt den Folder des Originals,
            // sizeBytes wird gleich aus den neuen Paragraphen berechnet.
            folderId: source.folderId || null,
            sizeBytes: sumParagraphSizes(newParagraphs.map(p => p.data))
        }

        await this._store.putProject(newProject)
        await this._store.putParagraphs(newParagraphs)

        return newProjectId
    }

    /**
     * Verschiebt ein Projekt in einen anderen Folder.
     * @param {string} projectId
     * @param {string|null} folderId - null = Root
     */
    async moveProject(projectId, folderId) {
        const project = await this._store.getProject(projectId)
        if (!project) return
        if ((project.folderId || null) === (folderId || null)) return
        project.folderId = folderId || null
        project.updatedAt = new Date().toISOString()
        await this._store.putProject(project)
        if (projectId === this.currentProjectId) {
            this._currentProject = project
        }
        // Cloud-Sync: Folder-Move signalisieren. SyncService schickt es im
        // naechsten Batch als projectFolderMove.
        if (this._syncService && project.cloudEnabled
                && typeof this._syncService.enqueueProjectFolderMove === 'function') {
            this._syncService.enqueueProjectFolderMove(projectId, folderId || null)
        }
    }

    /**
     * Haengt die in-memory Referenzen vom alten auf eine neue Projekt-UUID
     * um. Wird vom Cloud-Off-Pfad (Spec §10.1) gerufen, nachdem das
     * Projekt lokal eine neue UUID + ein neues Projekt-Objekt bekommen
     * hat. `_paragraphMeta` und `_dirtyParagraphs` sind Paragraph-ID-
     * keyed und bleiben gueltig — Paragraph-IDs aendern sich nicht.
     *
     * @param {string} oldId
     * @param {string} newId
     * @param {Object} newProject - das frisch geschriebene Projekt-Objekt
     */
    swapProjectId(oldId, newId, newProject) {
        if (this.currentProjectId === oldId) {
            this.currentProjectId = newId
            this._currentProject = newProject
        }
    }

    /**
     * Liefert einen sicheren Duplikat-Titel: "(copy)", "(2. copy)", …
     * Sucht alle aktiven Geschwister-Projekte im selben Folder und waehlt
     * das niedrigste freie Suffix.
     * @param {string} baseTitle
     * @param {string|null} folderId
     * @returns {Promise<string>}
     */
    async _suggestDuplicateTitle(baseTitle, folderId) {
        const siblings = await this._store.getAllProjects(this._userId)
        const sameFolder = siblings.filter(p => (p.folderId || null) === (folderId || null))
        const titles = new Set(sameFolder.map(p => p.title || ''))
        const first = `${baseTitle} (copy)`
        if (!titles.has(first)) return first
        for (let n = 2; n < 1000; n++) {
            const candidate = `${baseTitle} (${n}. copy)`
            if (!titles.has(candidate)) return candidate
        }
        return `${baseTitle} (copy)`  // pathologisch — Fallback
    }

    /**
     * Benennt ein Projekt um.
     * @param {string} id
     * @param {string} title
     */
    async renameProject(id, title) {
        const project = await this._store.getProject(id)
        if (!project) return

        project.title = title
        project.updatedAt = new Date().toISOString()
        await this._store.putProject(project)

        if (id === this.currentProjectId) {
            this._currentProject = project
        }

        // Cloud-Projekt: Rename zum Server pushen
        if (project.cloudEnabled) {
            try {
                await fetch(`/api/projects/${id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ title })
                })
            } catch {
                // Netzwerkfehler ignorieren — wird beim naechsten Sync korrigiert
            }
        }

        // UI-Sync-Hook (main.js registriert ihn beim Bootstrap): TabBar + Pane-
        // Border + metadata.title werden aktualisiert. So bleibt es egal, ob
        // ein Rename aus dem Dashboard, aus FileManager oder spaeter aus
        // anderen Pfaden kommt — die Konsistenz haengt nicht am Aufrufer.
        if (typeof this._onRenamed === 'function') {
            try { await this._onRenamed(id, title) } catch (err) {
                console.warn('[ProjectManager] _onRenamed hook failed:', err && err.message)
            }
        }
    }

    /**
     * Registriert einen Hook, der nach jedem erfolgreichen renameProject
     * gerufen wird (id, newTitle) => void|Promise. Pflegt UI-Sync zentral.
     */
    setOnRenamed(fn) {
        this._onRenamed = fn
    }

    /**
     * Verschiebt ein Projekt in den Papierkorb (Soft Delete).
     *
     * Wenn das aktive Projekt geloescht wird, muss zusaetzlich der RAM-Cache
     * `_currentProject.deletedAt` mit gesetzt werden — sonst schreibt der
     * naechste `flush()` (insbesondere der, den `switchToProject` intern
     * macht) das Objekt mit `deletedAt = null` wieder zurueck, und das
     * Projekt ist faktisch un-deleted.
     *
     * @param {string} id
     */
    async softDeleteProject(id) {
        const project = await this._store.getProject(id)
        if (!project) return

        project.deletedAt = new Date().toISOString()
        await this._store.putProject(project)

        if (id === this.currentProjectId && this._currentProject) {
            this._currentProject.deletedAt = project.deletedAt
        }
    }

    /**
     * Stellt ein Projekt aus dem Papierkorb wieder her.
     * @param {string} id
     */
    async restoreProject(id) {
        const project = await this._store.getProject(id)
        if (!project) return

        project.deletedAt = null
        project.updatedAt = new Date().toISOString()
        await this._store.putProject(project)
    }

    /**
     * Löscht ein Projekt permanent (mit allen Paragraphen).
     * @param {string} id
     */
    async permanentDeleteProject(id) {
        await this._store.deleteProject(id)
    }

    /**
     * Räumt den Papierkorb auf — löscht Projekte die älter als daysOld Tage sind.
     * @param {number} daysOld
     */
    async cleanupTrash(daysOld) {
        const deleted = await this._store.getDeletedProjects()
        const cutoff = Date.now() - daysOld * 24 * 60 * 60 * 1000

        for (const project of deleted) {
            if (new Date(project.deletedAt).getTime() < cutoff) {
                await this._store.deleteProject(project.id)
            }
        }
    }

    // ─────────────────────────────────────────────
    // Dirty-Tracking & Flush
    // ─────────────────────────────────────────────

    /**
     * Markiert einen Paragraphen als geändert.
     * @param {string} paragraphId
     */
    markDirty(paragraphId) {
        this._dirtyParagraphs.add(paragraphId)
    }

    /**
     * Markiert alle Paragraphen als geändert.
     * @param {string[]} paragraphIds
     */
    markAllDirty(paragraphIds) {
        for (const id of paragraphIds) {
            this._dirtyParagraphs.add(id)
        }
    }

    /**
     * Signalisiert eine strukturelle Aenderung (Absatz hinzugefuegt/geloescht/verschoben).
     * flush() erkennt dann neue Absaetze automatisch und aktualisiert die paragraphOrder.
     */
    markStructuralChange() {
        this._structuralDirty = true
    }

    /**
     * Setzt die Callback-Funktion die aktuelle Editor-Paragraphen liefert.
     * Muss einmal nach Editor-Initialisierung aufgerufen werden.
     * @param {Function} fn - () => Paragraph[]
     */
    setEditorParagraphsProvider(fn) {
        this._getEditorParagraphs = fn
    }

    /**
     * Setzt den SyncService fuer Cloud-Sync.
     * @param {import('./SyncService.js').SyncService} syncService
     */
    setSyncService(syncService) {
        this._syncService = syncService
    }

    /**
     * Fuehrt sofort einen Flush mit dem registrierten Editor-Provider aus.
     * Wird z.B. vom Cloud-Toggle aufgerufen, um pending dirty-writes zu
     * drainen, BEVOR cloudEnabled=true gesetzt wird — sonst wuerde der
     * naechste regulaere Flush alle dirty-Paragraphen als Pending enqueuen.
     * @returns {Promise<void>}
     */
    async flushNow() {
        return this.flush(this._getEditorParagraphs)
    }

    /**
     * Plant einen Flush mit 1s-Debounce.
     */
    scheduleFlush() {
        if (this._flushTimeout) {
            clearTimeout(this._flushTimeout)
        }
        this._flushTimeout = setTimeout(() => {
            this._flushTimeout = null
            this.flush(this._getEditorParagraphs)
        }, FLUSH_DELAY)
    }

    /**
     * Schreibt alle geänderten Paragraphen in IndexedDB.
     * Wird vom Debounce-Timer oder bei Projektwechsel aufgerufen.
     *
     * @param {Function} [getEditorParagraphs] - Funktion die aktuelle Paragraphen-Daten liefert
     */
    async flush(getEditorParagraphs) {
        if (this._dirtyParagraphs.size === 0 && !this._structuralDirty) return
        if (!this.currentProjectId) return

        const endFlush = mark('ProjectManager.flush.total')

        // Pending Timeout abbrechen
        if (this._flushTimeout) {
            clearTimeout(this._flushTimeout)
            this._flushTimeout = null
        }

        // B6 (System-Review 2026-06-10): Dirty-State synchron KONSUMIEREN
        // statt am Ende pauschal zu clearen. Marks, die waehrend der awaits
        // im Schreibpfad gesetzt werden (Tastendruck im Flush-Fenster),
        // landen in den frischen Live-Feldern und gehen nicht mehr verloren
        // — der naechste Flush schreibt sie. Konsumiert wird nur, wenn ein
        // Provider da ist und damit tatsaechlich geschrieben werden kann.
        let dirtyIds = new Set()
        let structuralDirty = false
        if (getEditorParagraphs) {
            dirtyIds = this._dirtyParagraphs
            this._dirtyParagraphs = new Set()
            structuralDirty = this._structuralDirty
            this._structuralDirty = false
        }

        try {
            await this._writeFlush(getEditorParagraphs, dirtyIds, structuralDirty)
        } catch (err) {
            // Fehlgeschlagener Write (z.B. IndexedDB-Quota): konsumierte
            // Marks zuruecklegen, damit der naechste Flush sie erneut
            // versucht — sonst lebte die Tipparbeit nur noch im In-Memory-
            // Modell und ginge beim Reload verloren.
            for (const id of dirtyIds) this._dirtyParagraphs.add(id)
            if (structuralDirty) this._structuralDirty = true
            // Meta-Cache verwerfen: die Serialisierungs-Schleife hat
            // Versionen/Hashes schon optimistisch gebumpt — der naechste
            // Flush muss den echten Store-Stand sehen, sonst skippt der
            // Hash-Vergleich den Retry.
            this._paragraphMeta = null
            throw err
        } finally {
            endFlush()
        }
    }

    /**
     * Schreibpfad des Flushs — arbeitet ausschliesslich auf den von flush()
     * konsumierten Snapshots (B6), nie auf den Live-Dirty-Feldern.
     *
     * @param {Function} getEditorParagraphs
     * @param {Set<string>} dirtyIds - konsumierte Dirty-Marks
     * @param {boolean} structuralDirty - konsumiertes Struktur-Flag
     * @private
     */
    async _writeFlush(getEditorParagraphs, dirtyIds, structuralDirty) {
        // Paragraphen-Daten vom Editor holen
        let flushedParagraphIds = []
        let deletedParagraphIds = []
        const needsParagraphWrite = dirtyIds.size > 0 || structuralDirty
        if (getEditorParagraphs && needsParagraphWrite) {
            const editorParagraphs = getEditorParagraphs()
            const now = new Date().toISOString()

            // Paragraph-Meta aus In-Memory-Cache nutzen — im Happy Path kein
            // DB-Read. Wurde bei Remote-Sync invalidiert? Dann einmalig ueber
            // getParagraphsByProject rehydrieren: wir brauchen nicht nur die
            // Versionen, sondern auch die Content-Hashes und sizeBytes (fuer
            // das Delta-Tracking aus Spec §2.2.1) — der leichtere
            // getParagraphIdVersionMap-Pfad reicht nicht mehr.
            if (!this._paragraphMeta) {
                this._paragraphMeta = await measure(
                    'ProjectManager.flush.rehydrateParagraphMeta',
                    async () => {
                        const rows = await this._store.getParagraphsByProject(this.currentProjectId)
                        return new Map(rows.map(p => [p.id, {
                            version: p.version || 1,
                            hash: hashParagraphData(p.data),
                            sizeBytes: paragraphSize(p.data)
                        }]))
                    },
                    r => r.size
                )
            }
            const paragraphMeta = this._paragraphMeta

            // Delta-Tracker fuer project.sizeBytes — wird am Ende des Flushs
            // einmal auf das Project-Objekt angewendet, NICHT pro Paragraph.
            let sizeDelta = 0

            // Paragraphen serialisieren und schreiben
            const paragraphsToWrite = []
            const paragraphOrder = []

            for (const para of editorParagraphs) {
                paragraphOrder.push(para.id)

                const isDirty = dirtyIds.has(para.id)
                if (!isDirty && !structuralDirty) continue

                // Serialisieren + Hashen ist der Preis fuer den Filter — im
                // Happy Path sowieso unvermeidbar (wir wollen schreiben), und
                // im Undo-Pfad ist das der ganze Witz: 4674 Paragraphen
                // hashen (~10 ms) statt alle 4674 zu schreiben (~260 ms).
                const meta = paragraphMeta.get(para.id)
                const data = serializeParagraphRT(para, { includeProjectState: true })
                const newHash = hashParagraphData(data)

                // Skip: Content real unveraendert. Kein Write, kein Version-
                // Bump (Lesson 45), kein Sync-Push. Gilt sowohl fuer den
                // Undo-Fall (alle IDs dirty, aber 4672 identisch) als auch
                // fuer den Happy Path (Zeichen getippt + sofort geloescht).
                if (meta && meta.hash === newHash) continue

                const newVersion = (meta?.version || 0) + 1
                const newSize = paragraphSize(data)
                // Delta-Update fuer project.sizeBytes (Spec §2.2.1):
                // - bei Insert: keine alte Groesse → +newSize
                // - bei Update: +newSize − oldSize
                sizeDelta += newSize - (meta?.sizeBytes || 0)
                paragraphsToWrite.push({
                    id: para.id,
                    projectId: this.currentProjectId,
                    data,
                    version: newVersion,
                    updatedAt: now
                })
                paragraphMeta.set(para.id, { version: newVersion, hash: newHash, sizeBytes: newSize })
            }

            if (paragraphsToWrite.length > 0) {
                await this._store.putParagraphs(paragraphsToWrite)
                flushedParagraphIds = paragraphsToWrite.map(p => p.id)
            }

            // Geloeschte Paragraphen erkennen (Vergleich alte vs. neue paragraphOrder)
            if (this._currentProject) {
                const oldOrder = new Set(this._currentProject.paragraphOrder || [])
                const newOrderSet = new Set(paragraphOrder)
                deletedParagraphIds = [...oldOrder].filter(id => !newOrderSet.has(id))

                // Geloeschte Paragraphen aus IndexedDB entfernen + sizeBytes-
                // Delta entsprechend abziehen (Spec §2.2.1).
                for (const id of deletedParagraphIds) {
                    const oldMeta = paragraphMeta.get(id)
                    if (oldMeta) sizeDelta -= (oldMeta.sizeBytes || 0)
                    await this._store.deleteParagraph(id)
                    paragraphMeta.delete(id)
                }
            }

            // paragraphOrder + sizeBytes im Projekt aktualisieren.
            if (this._currentProject) {
                this._currentProject.paragraphOrder = paragraphOrder
                this._currentProject.updatedAt = now
                if (sizeDelta !== 0) {
                    const next = (this._currentProject.sizeBytes || 0) + sizeDelta
                    this._currentProject.sizeBytes = Math.max(0, next)
                }
                await this._store.putProject(this._currentProject)
            }
        }

        // Cloud-Sync: geaenderte Paragraphen zum SyncService melden
        if (this._syncService && this._currentProject?.cloudEnabled && flushedParagraphIds.length > 0) {
            this._syncService.enqueueParagraphs(this.currentProjectId, flushedParagraphIds)
        }

        // Cloud-Sync: geloeschte Paragraphen melden
        if (this._syncService && this._currentProject?.cloudEnabled && deletedParagraphIds?.length > 0) {
            this._syncService.enqueueDeletes(this.currentProjectId, deletedParagraphIds)
        }

        // Cloud-Sync: Reorder signalisieren (paragraphOrder hat sich geaendert)
        if (this._syncService && this._currentProject?.cloudEnabled && structuralDirty) {
            this._syncService.enqueueReorder(this.currentProjectId)
        }
    }

    /**
     * Speichert den Cursor-State im aktuellen Projekt.
     * @param {Object} cursorState
     */
    async saveProjectState(cursorState) {
        if (!this.currentProjectId || !this._currentProject) return

        this._currentProject.cursorState = cursorState
        this._currentProject.updatedAt = new Date().toISOString()
        await this._store.putProject(this._currentProject)
    }

    /**
     * Gibt das aktuelle Projekt-Objekt zurück (aus Cache).
     * @returns {Object|null}
     */
    getCurrentProject() {
        return this._currentProject
    }

    /**
     * Alle aktiven Projekte des aktuellen Users (ohne gelöschte).
     * @returns {Promise<Object[]>}
     */
    async getAllProjects() {
        return this._store.getAllProjects(this._userId)
    }

    /**
     * Liefert die Projekt-Metadaten (ohne Paragraphen) aus dem Store.
     * @param {string} projectId
     * @returns {Promise<Object|null>}
     */
    async getProject(projectId) {
        return this._store.getProject(projectId)
    }

    /**
     * Alle gelöschten Projekte des aktuellen Users (Papierkorb).
     * @returns {Promise<Object[]>}
     */
    async getDeletedProjects() {
        return this._store.getDeletedProjects(this._userId)
    }

    /**
     * Alle Paragraphen eines Projekts.
     * @param {string} projectId
     * @returns {Promise<Object[]>}
     */
    async getParagraphsByProject(projectId) {
        return this._store.getParagraphsByProject(projectId)
    }

    /**
     * Projekt-Objekt aus Store laden.
     * @param {string} projectId
     * @returns {Promise<Object|undefined>}
     */
    async getProject(projectId) {
        return this._store.getProject(projectId)
    }

    /**
     * Projekt-Objekt speichern.
     * @param {Object} project
     */
    async putProject(project) {
        await this._store.putProject(project)
        // Cache aktualisieren falls es das aktive Projekt ist
        if (project.id === this.currentProjectId) {
            this._currentProject = project
        }
    }

    // ─────────────────────────────────────────────
    // Migration von localStorage
    // ─────────────────────────────────────────────

    /**
     * Einmalige Migration: localStorage → IndexedDB.
     * Liest carbisdale_content (v5) und carbisdale_editor_state,
     * erzeugt ein Projekt mit neuen UUIDs für alle Paragraphen.
     */
    async _migrateFromLocalStorage() {
        try {
            const contentRaw = localStorage.getItem(CONTENT_KEY)
            if (!contentRaw) {
                // Kein Content vorhanden — nichts zu migrieren
                localStorage.setItem(MIGRATED_KEY, 'true')
                return
            }

            const content = JSON.parse(contentRaw)
            if (!content || content.version !== 5 || !Array.isArray(content.paragraphs)) {
                // Unbekanntes Format
                localStorage.setItem(MIGRATED_KEY, 'true')
                return
            }

            // Editor-State laden
            let editorState = null
            const stateRaw = localStorage.getItem(EDITOR_STATE_KEY)
            if (stateRaw) {
                try { editorState = JSON.parse(stateRaw) } catch { /* ignorieren */ }
            }

            // Projekt erstellen
            const projectId = generateUUID()
            const now = new Date().toISOString()
            const title = editorState?.title || 'untitled'

            // Paragraphen mit neuen UUIDs anlegen
            const idMap = new Map()  // alte ID → neue UUID
            const paragraphOrder = []
            const paragraphs = content.paragraphs.map((data, index) => {
                const newId = generateUUID()
                if (data.id != null) {
                    idMap.set(data.id, newId)
                }
                paragraphOrder.push(newId)

                return {
                    id: newId,
                    projectId,
                    data: { ...data, id: undefined },  // id aus data entfernen, liegt auf Top-Level
                    version: 1,
                    updatedAt: now
                }
            })

            // Section Links umschreiben (targetId alte→neue ID)
            for (const p of paragraphs) {
                if (p.data.links) {
                    for (const field of ['text', 'note', 'info']) {
                        if (p.data.links[field]) {
                            p.data.links[field] = p.data.links[field].map(link => ({
                                ...link,
                                targetId: idMap.has(link.targetId) ? idMap.get(link.targetId) : link.targetId
                            }))
                        }
                    }
                }
            }

            // Cursor-State migrieren (Paragraph-Index → Paragraph-UUID)
            let cursorState = null
            if (editorState && editorState.paragraphIndex != null) {
                const cursorParagraphId = paragraphOrder[editorState.paragraphIndex]
                if (cursorParagraphId) {
                    cursorState = {
                        paragraphId: cursorParagraphId,
                        offset: editorState.offset || 0,
                        cursorScreenY: editorState.cursorScreenY || 0
                    }
                }
            }

            const project = {
                id: projectId,
                title,
                author: '',
                userId: this._userId,
                paragraphOrder,
                cursorState,
                cloudEnabled: false,
                version: 1,
                syncStatus: 'local',
                createdAt: now,
                updatedAt: now,
                deletedAt: null
            }

            // In IndexedDB schreiben
            await this._store.putProject(project)
            await this._store.putParagraphs(paragraphs)

            // Tab-State initialisieren
            localStorage.setItem(TABS_KEY, JSON.stringify({
                openTabs: [projectId],
                activeTab: projectId
            }))

            // Letztes Projekt merken
            localStorage.setItem(LAST_PROJECT_KEY, projectId)

            // Migration als erledigt markieren
            localStorage.setItem(MIGRATED_KEY, 'true')

            console.log(`Migration: ${paragraphs.length} Paragraphen → IndexedDB (Projekt: ${title})`)
        } catch (error) {
            console.error('Migration von localStorage fehlgeschlagen:', error)
            // Migration trotzdem als erledigt markieren, damit kein Loop entsteht
            localStorage.setItem(MIGRATED_KEY, 'true')
        }
    }

    /**
     * Weist bestehenden Projekten ohne userId den aktuellen User zu.
     * Wird bei jedem initialize() aufgerufen — nur Projekte ohne userId werden angefasst.
     */
    async _migrateUserIds() {
        const all = await this._store.getAllProjects()  // ohne userId-Filter
        for (const project of all) {
            if (!project.userId) {
                project.userId = this._userId
                await this._store.putProject(project)
            }
        }

        // Auch geloeschte Projekte migrieren
        const deleted = await this._store.getDeletedProjects()  // ohne userId-Filter
        for (const project of deleted) {
            if (!project.userId) {
                project.userId = this._userId
                await this._store.putProject(project)
            }
        }
    }
}
